import React, { useEffect, useRef, useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  IconButton, 
  TextField, 
  Button, 
  Stack, 
  CircularProgress, 
  Avatar, 
  Badge,
  Tooltip,
  Divider,
  Chip
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import MinimizeIcon from '@mui/icons-material/Minimize';
import { useAuth } from '../context/AuthContext';
import { sendLiveChatMessage, subscribeToLiveChatMessages, createOrUpdateChatSession } from '../firebase/services';

interface Message {
  id: string;
  text: string;
  createdAt: any;
  userId: string;
  displayName: string;
  isStaff: boolean;
  avatar?: string;
}

const LiveChat: React.FC = () => {
  const { currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageIdRef = useRef<string | null>(null);

  // Utilise l'ID utilisateur comme session de tchat
  const sessionId = currentUser?.uid;

  useEffect(() => {
    if (!sessionId || !currentUser) return;
    
    // Crée ou met à jour la session de tchat
    const initSession = async () => {
      await createOrUpdateChatSession({
        userId: currentUser.uid,
        displayName: currentUser.displayName || 'Utilisateur',
        email: currentUser.email || undefined,
        lastActivity: new Date(),
        status: 'active'
      });
    };
    
    initSession();
  }, [sessionId, currentUser]);

  useEffect(() => {
    if (!sessionId) return;
    setLoading(true);
    
    const unsubscribe = subscribeToLiveChatMessages(sessionId, (msgs) => {
      setMessages(msgs);
      setLoading(false);
      
      // Vérifie si de nouveaux messages sont arrivés
      if (msgs.length > 0) {
        const lastMessage = msgs[msgs.length - 1];
        
        // Si le dernier message est nouveau et n'est pas de l'utilisateur actuel
        if (lastMessageIdRef.current !== lastMessage.id && 
            lastMessage.userId !== currentUser?.uid && 
            !open) {
          setUnreadCount(prev => prev + 1);
        }
        
        lastMessageIdRef.current = lastMessage.id;
      }
    });
    
    return () => unsubscribe();
  }, [sessionId, currentUser, open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  // Réinitialise le compteur de messages non lus lorsque le chat est ouvert
  useEffect(() => {
    if (open) {
      setUnreadCount(0);
    }
  }, [open]);

  const handleSend = async () => {
    if (!input.trim() || !sessionId || !currentUser) return;
    setSending(true);
    await sendLiveChatMessage(sessionId, {
      text: input,
      userId: currentUser.uid,
      displayName: currentUser.displayName || 'Vous',
      isStaff: false,
    });
    setInput('');
    setSending(false);
  };

  const toggleMinimize = () => {
    setMinimized(!minimized);
    if (!minimized) {
      setUnreadCount(0);
    }
  };

  const formatMessageTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diff < 60) {
      return 'Il y a ' + diff + ' secondes';
    } else if (diff < 3600) {
      return 'Il y a ' + Math.floor(diff / 60) + ' minutes';
    } else if (diff < 86400) {
      return 'Il y a ' + Math.floor(diff / 3600) + ' heures';
    } else {
      return 'Le ' + date.toLocaleDateString();
    }
  };

  return (
    <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}>
      {!open && (
        <Badge badgeContent={unreadCount} color="error" overlap="circular">
          <IconButton 
            color="primary" 
            size="large" 
            onClick={() => setOpen(true)} 
            sx={{ 
              bgcolor: 'white', 
              boxShadow: 3,
              '&:hover': {
                bgcolor: 'primary.light',
                color: 'white'
              }
            }}
          >
            <ChatIcon fontSize="large" />
          </IconButton>
        </Badge>
      )}
      {open && (
        <Paper 
          elevation={8} 
          sx={{ 
            width: 350, 
            height: minimized ? 'auto' : 480, 
            display: 'flex', 
            flexDirection: 'column', 
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <Box 
            sx={{ 
              p: 2, 
              borderBottom: 1, 
              borderColor: 'divider', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              bgcolor: 'primary.main', 
              color: 'white', 
              borderTopLeftRadius: 12, 
              borderTopRightRadius: 12 
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChatIcon />
              <Typography variant="subtitle1" fontWeight={600}>
                Assistance en direct
              </Typography>
              <Chip 
                label="En ligne" 
                size="small" 
                sx={{ 
                  height: 20, 
                  fontSize: '0.7rem', 
                  bgcolor: 'success.main', 
                  color: 'white' 
                }} 
              />
            </Box>
            <Box>
              <IconButton size="small" onClick={toggleMinimize} sx={{ color: 'white', mr: 1 }}>
                <MinimizeIcon />
              </IconButton>
              <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          
          {!minimized && (
            <>
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#f5f8fa' }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : messages.length > 0 ? (
                  <Stack spacing={1.5}>
                    {messages.map((msg, index) => {
                      const isCurrentUser = msg.userId === currentUser?.uid;
                      const showAvatar = index === 0 || messages[index - 1].userId !== msg.userId;
                      
                      return (
                        <Box key={msg.id}>
                          {(index === 0 || new Date(messages[index-1].createdAt.toDate()).toDateString() !== new Date(msg.createdAt.toDate()).toDateString()) && (
                            <Divider sx={{ my: 2 }}>
                              <Chip 
                                label={new Date(msg.createdAt.toDate()).toLocaleDateString()} 
                                size="small" 
                                variant="outlined" 
                              />
                            </Divider>
                          )}
                          
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              flexDirection: isCurrentUser ? 'row-reverse' : 'row', 
                              alignItems: 'flex-end',
                              mb: 1
                            }}
                          >
                            {showAvatar && (
                              <Tooltip title={msg.displayName} arrow placement={isCurrentUser ? 'left' : 'right'}>
                                <Avatar 
                                  sx={{ 
                                    width: 28, 
                                    height: 28, 
                                    bgcolor: msg.isStaff ? 'primary.main' : 'grey.400', 
                                    fontSize: 14,
                                    visibility: showAvatar ? 'visible' : 'hidden'
                                  }}
                                >
                                  {msg.displayName?.charAt(0).toUpperCase()}
                                </Avatar>
                              </Tooltip>
                            )}
                            {!showAvatar && <Box sx={{ width: 28 }} />}
                            
                            <Box sx={{ mx: 1, maxWidth: '75%' }}>
                              <Paper 
                                sx={{ 
                                  p: 1.5, 
                                  bgcolor: isCurrentUser ? 'primary.light' : 'white', 
                                  color: isCurrentUser ? 'white' : 'text.primary', 
                                  borderRadius: 2,
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                }}
                              >
                                <Typography variant="body2">{msg.text}</Typography>
                              </Paper>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  display: 'block', 
                                  mt: 0.5, 
                                  color: 'text.secondary',
                                  textAlign: isCurrentUser ? 'right' : 'left',
                                  fontSize: '0.65rem'
                                }}
                              >
                                {formatMessageTime(msg.createdAt)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </Stack>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%',
                    color: 'text.secondary'
                  }}>
                    <ChatIcon sx={{ fontSize: 40, mb: 2, opacity: 0.5 }} />
                    <Typography variant="body2">
                      Commencez à discuter avec notre équipe d'assistance
                    </Typography>
                  </Box>
                )}
              </Box>
              
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Votre message..."
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    disabled={sending}
                    multiline
                    maxRows={3}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 3,
                        fontSize: '0.9rem'
                      } 
                    }}
                  />
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSend} 
                    disabled={sending || !input.trim()}
                    sx={{ borderRadius: '50%', minWidth: 0, width: 40, height: 40, p: 0 }}
                  >
                    <SendIcon />
                  </Button>
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default LiveChat; 